import { FC, Fragment } from "react";
import { IComparisonColumn, IComparisonRow } from "../../../data/crystalPageContent";
import styles from "./index.module.css";

interface IProps {
  title: string;
  columns: IComparisonColumn[];
  rows: IComparisonRow[];
  // Only the Gold Plus/Crystal SE table (see the comment below) is speced
  // as fixed-height and internally-scrolling; other tables using this same
  // component should just expand to fit all their rows.
  scrollable?: boolean;
}

// Paragraphs are separated by a blank line; within a paragraph, single
// linebreaks stack tightly, and a "Requires: ..." line is bolded (matches
// how the design calls out hardware requirements under "Depends on hardware").
function renderCellText(text: string) {
  return text.split("\n\n").map((para, i) => (
    <p key={i}>
      {para.split("\n").map((line, j, arr) => (
        <span key={j}>
          {line.startsWith("Requires:") ? <strong>{line}</strong> : line}
          {j < arr.length - 1 && <br />}
        </span>
      ))}
    </p>
  ));
}

// A fixed-height (800px), internally-scrolling comparison table, per the
// "Difference between Firewalla Gold Plus & Crystal SE" spec in
// https://github.com/firewalla/firecommit/issues/9484. Only the row data
// scrolls - the column header (with the Gold Plus / Crystal SE title +
// logo) stays fixed above it, not inside the scrollable area.
const ComparisonTable: FC<IProps> = ({ title, columns, rows, scrollable = true }) => {
  // minmax(0, ...) matters here: a bare `fr` track has an implicit min
  // width equal to its content's min-content size, so a long unbreakable
  // string (or just a narrow viewport) can force the grid wider than
  // `.table-wrap`, which silently clips it (overflow: hidden) instead of
  // wrapping the text.
  const gridTemplateColumns = `minmax(0, 1.2fr) repeat(${columns.length}, minmax(0, 1fr))`;
  return (
    <section className={styles["section"]} style={{ width: "100%", maxWidth: 1200 }}>
      <h2 className={styles["title"]}>{title}</h2>
      <div className={styles["table-wrap"]}>
        <div className={styles["grid"]} style={{ gridTemplateColumns }}>
          <div className={`${styles["cell"]} ${styles["header-cell"]}`} />
          {columns.map((col) => (
            <div
              key={col.key}
              className={`${styles["cell"]} ${styles["header-cell"]}`}
            >
              <div className={styles["col-label"]}>{col.label}</div>
              {col.logo && (
                <img
                  src={col.logo}
                  alt={col.label}
                  className={styles["col-logo"]}
                  loading="lazy"
                />
              )}
            </div>
          ))}
        </div>
        <div className={scrollable ? styles["scroll-area"] : undefined}>
          <div className={styles["grid"]} style={{ gridTemplateColumns }}>
            {rows.map((row, rowIndex) => (
              <Fragment key={row.label}>
                <div
                  className={`${styles["cell"]} ${styles["row-label"]} ${rowIndex % 2 === 0 ? styles["shaded"] : ""
                    }`}
                >
                  {row.label}
                </div>
                {columns.map((col) => (
                  <div
                    key={col.key}
                    className={`${styles["cell"]} ${rowIndex % 2 === 0 ? styles["shaded"] : ""
                      }`}
                  >
                    {row.values[col.key] === "✓" ? (
                      <img
                        src="/images/personal-feature-check.svg"
                        alt="Included"
                        className={styles["check"]}
                      />
                    ) : typeof row.values[col.key] === "string" ? (
                      renderCellText(String(row.values[col.key]))
                    ) : (
                      row.values[col.key]
                    )}
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonTable;
